/**
 * ShareToolsPage
 * Standalone page (route: /dealer/share-tools) showing the 3 public free-tool
 * share URLs with copy + open buttons. Accessed from the dealer dashboard
 * sidebar / a button in the Resources tab.
 *
 * All cards inlined as flat JSX to avoid the dev-server visual-edits Babel
 * plugin choking on dynamic Icon-as-prop patterns.
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calculator, FileSearch, MessageSquare, Copy, Check, ExternalLink, Sparkles, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';

const ShareToolsPage = () => {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [copiedSlug, setCopiedSlug] = useState(null);
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';

  const copy = async (slug) => {
    const url = baseUrl + '/free/' + slug;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedSlug(slug);
      toast.success('Link copied!');
      setTimeout(() => setCopiedSlug(null), 2000);
    } catch (e) {
      toast.error('Could not copy link');
    }
  };

  const cardBg = isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200 shadow-sm';
  const codeBg = isDark ? 'bg-gray-900 text-gray-300' : 'bg-gray-50 text-gray-700';
  const previewBtnClass = isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-white border border-gray-300 hover:bg-gray-50 text-gray-800';
  const headerBg = isDark
    ? 'bg-gradient-to-r from-blue-900/30 to-amber-500/10 border border-amber-500/30'
    : 'bg-gradient-to-r from-blue-50 to-amber-50 border border-amber-300';
  const copyBtnFor = (slug) => {
    const copied = copiedSlug === slug;
    return copied
      ? 'bg-green-500 text-white'
      : isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800';
  };

  const url1 = baseUrl + '/free/cost-calculator';
  const url2 = baseUrl + '/free/deal-analyzer';
  const url3 = baseUrl + '/free/negotiation-coach';
  const c1 = copiedSlug === 'cost-calculator';
  const c2 = copiedSlug === 'deal-analyzer';
  const c3 = copiedSlug === 'negotiation-coach';

  return (
    <div className={'min-h-screen ' + (isDark ? 'bg-gray-900' : 'bg-gray-50')} data-testid="share-tools-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Back button */}
        <button
          onClick={() => navigate('/dealer?tab=resources')}
          className={'inline-flex items-center gap-2 mb-6 text-sm font-medium ' + (isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900')}
          data-testid="share-tools-back-btn"
        >
          <ArrowLeft size={16} />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className={'rounded-xl p-5 mb-6 ' + headerBg} data-testid="share-tools-header">
          <div className="flex items-start gap-3">
            <Sparkles size={22} className="text-amber-500 mt-0.5 shrink-0" />
            <div>
              <h1 className={'text-xl sm:text-2xl font-extrabold ' + (isDark ? 'text-white' : 'text-blue-900')}>
                Share Lead-Magnet Tool Links
              </h1>
              <p className={'text-sm sm:text-base mt-2 ' + (isDark ? 'text-gray-300' : 'text-gray-700')}>
                Drop these public links into Facebook RV groups, email blasts, Instagram bio, Reddit comments, or send directly to a hot lead. Anyone who unlocks gets captured as a lead in your system.
              </p>
            </div>
          </div>
        </div>

        {/* Three tool cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Cost Calculator */}
          <div className={'rounded-xl p-4 ' + cardBg} data-testid="share-tool-cost-calculator">
            <div className="flex items-start gap-3 mb-3">
              <div className={'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-green-600 ' + (isDark ? 'bg-green-500/20' : 'bg-green-100')}>
                <Calculator size={18} />
              </div>
              <div className="min-w-0">
                <h4 className={'font-semibold ' + (isDark ? 'text-white' : 'text-gray-900')}>RV Cost Calculator</h4>
                <p className={'text-xs ' + (isDark ? 'text-gray-400' : 'text-gray-500')}>See the true 5-year cost of an RV</p>
              </div>
            </div>
            <code className={'block text-xs font-mono px-2 py-1.5 rounded mb-3 break-all ' + codeBg} data-testid="share-tool-url-cost-calculator">
              {url1}
            </code>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => copy('cost-calculator')}
                className={'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ' + copyBtnFor('cost-calculator')}
                data-testid="copy-link-cost-calculator"
              >
                {c1 ? <Check size={14} /> : <Copy size={14} />}
                {c1 ? 'Copied!' : 'Copy Link'}
              </button>
              <a href={url1} target="_blank" rel="noopener noreferrer" className={'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ' + previewBtnClass} data-testid="preview-link-cost-calculator" title="Open in new tab">
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Deal Analyzer */}
          <div className={'rounded-xl p-4 ' + cardBg} data-testid="share-tool-deal-analyzer">
            <div className="flex items-start gap-3 mb-3">
              <div className={'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-blue-600 ' + (isDark ? 'bg-blue-500/20' : 'bg-blue-100')}>
                <FileSearch size={18} />
              </div>
              <div className="min-w-0">
                <h4 className={'font-semibold ' + (isDark ? 'text-white' : 'text-gray-900')}>RV Deal Analyzer</h4>
                <p className={'text-xs ' + (isDark ? 'text-gray-400' : 'text-gray-500')}>Is this listing a steal or a rip-off?</p>
              </div>
            </div>
            <code className={'block text-xs font-mono px-2 py-1.5 rounded mb-3 break-all ' + codeBg} data-testid="share-tool-url-deal-analyzer">
              {url2}
            </code>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => copy('deal-analyzer')}
                className={'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ' + copyBtnFor('deal-analyzer')}
                data-testid="copy-link-deal-analyzer"
              >
                {c2 ? <Check size={14} /> : <Copy size={14} />}
                {c2 ? 'Copied!' : 'Copy Link'}
              </button>
              <a href={url2} target="_blank" rel="noopener noreferrer" className={'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ' + previewBtnClass} data-testid="preview-link-deal-analyzer" title="Open in new tab">
                <ExternalLink size={14} />
              </a>
            </div>
          </div>

          {/* Negotiation Coach */}
          <div className={'rounded-xl p-4 ' + cardBg} data-testid="share-tool-negotiation-coach">
            <div className="flex items-start gap-3 mb-3">
              <div className={'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 text-purple-600 ' + (isDark ? 'bg-purple-500/20' : 'bg-purple-100')}>
                <MessageSquare size={18} />
              </div>
              <div className="min-w-0">
                <h4 className={'font-semibold ' + (isDark ? 'text-white' : 'text-gray-900')}>RV Negotiation Coach</h4>
                <p className={'text-xs ' + (isDark ? 'text-gray-400' : 'text-gray-500')}>Word-for-word dealership script</p>
              </div>
            </div>
            <code className={'block text-xs font-mono px-2 py-1.5 rounded mb-3 break-all ' + codeBg} data-testid="share-tool-url-negotiation-coach">
              {url3}
            </code>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => copy('negotiation-coach')}
                className={'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ' + copyBtnFor('negotiation-coach')}
                data-testid="copy-link-negotiation-coach"
              >
                {c3 ? <Check size={14} /> : <Copy size={14} />}
                {c3 ? 'Copied!' : 'Copy Link'}
              </button>
              <a href={url3} target="_blank" rel="noopener noreferrer" className={'flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ' + previewBtnClass} data-testid="preview-link-negotiation-coach" title="Open in new tab">
                <ExternalLink size={14} />
              </a>
            </div>
          </div>
        </div>

        <p className={'text-xs italic mt-4 ' + (isDark ? 'text-gray-500' : 'text-gray-500')}>
          Tip: Track unlock counts and conversion rates under <Link to="/admin" className="font-semibold underline">Admin → Free Tools</Link> (super-admin only).
        </p>
      </div>
    </div>
  );
};

export default ShareToolsPage;
